"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Layers, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const solutions = [{
  title: "Process Analytical Technology",
  description: "Real-time in-line and at-line process monitoring, spectroscopic analysis, and automated quality verification for pharmaceutical and chemical manufacturing.",
  link: "/ProcessAnalyticalTechnologyPage",
  badge: "Process Monitoring",
  icon: Activity,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solution_overview.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solution_overview.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/pat_solution_overview.png-wvc-sizes",
  alt: "Process analytical technology instrumentation in manufacturing plant",
  highlights: ["In-situ reaction tracking & endpoint detection", "Non-destructive real-time batch validation", "Seamless integration with manufacturing systems"]
}, {
  title: "Safety and Security",
  description: "Rapid optical identification tools, narcotics detection, hazardous substance screening, and compliance safety systems for controlled environments and field teams.",
  link: "/SafetyAndSecurityPage",
  badge: "Field & Facility Protection",
  icon: ShieldCheck,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_solution.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_solution.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/safety_security_solution.png-wvc-sizes",
  alt: "Handheld safety screening and chemical identification system",
  highlights: ["Instant through-barrier chemical identification", "Ruggedized handheld analyzers for harsh field work", "Comprehensive regulatory & traceability compliance"]
}];
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedsolutions" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1">
                <Layers className="size-3.5 text-primary" />
                <span>Integrated Capabilities</span>
              </Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore Related Solutions
            </h2>
            <p className="mt-3 text-base md:text-lg text-muted-foreground">
              Discover complementary analytical workflows and field-proven verification systems engineered for regulated industries.
            </p>
          </div>

          <div>
            <Button variant="outline" asChild className="group">
              <Link to="/SolutionsPage" className="inline-flex items-center gap-2">
                <span>View all solutions</span>
                <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {solutions.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border shadow-md hover:shadow-lg transition-[border-color,box-shadow] duration-200">
                  <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                    <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-background/95 text-foreground shadow-xs backdrop-blur-xs font-medium">
                        {item.badge}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-xl md:text-2xl font-bold font-default">
                        {item.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-base text-muted-foreground leading-relaxed mt-2">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1">
                    <div className="border-t border-border/60 pt-4 mt-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Key Advantages
                      </p>
                      <ul className="space-y-2.5">
                        {item.highlights.map((highlight, hIndex) => <li key={hIndex} data-index={hIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-4 border-t border-border">
                    <Button asChild className="w-full sm:w-auto group">
                      <Link to={item.link} className="inline-flex items-center justify-center gap-2">
                        <span>Explore {item.title}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}